import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Button, Stack, Typography } from "@mui/material";
import EmptyCardDealWorkspace from "assets/images/EmptyCardDealWorkspace.png";
import { WorkspaceFolderCloseIcon } from "assets/material-custom-icons";
import { ColumnVisibilityMenu } from "components/Deal-workspace/Menus/ColumnVisibilityMenu";
import { MetadataTable } from "components/Deal-workspace/Workspace-metadata-components/MetadataTable";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { Menu } from "components/popups";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { EntityType } from "constants/index";
import { Routes } from "constants/routes";
import { useHighlightAfterClose } from "hooks";
import { usePickUserPermissions } from "store";
import { FolderKind, UserPermissionType } from "store/types";

import { useWorkspaceFilter } from "../Deal-workspace/Hooks/use-workspace-filter";
import { DocumentActions, Props as DocumentActionsProps } from "../Deal-workspace/Menus/DocumentActions";
import { FileActions, Props as FileActionsProps } from "../Deal-workspace/Menus/FileActions";
import { FolderActions, Props as FolderActionsProps } from "../Deal-workspace/Menus/FolderActions";
import { FormActions, Props as FormActionsProps } from "../Deal-workspace/Menus/FormActions";
import { BulkActionsBar } from "../Deal-workspace/Workspace-metadata-components/BulkActionsBar";
import { WorkspaceFilter } from "../Deal-workspace/Workspace-metadata-components/WorkspaceFilter";
import { useBulkFileActionsHook } from "./hooks/use-bulk-file-actions-hook";
import { useWorkspaceBulkActionsHook } from "./hooks/use-workspace-bulk-actions-hook";
import { useWorkspaceCellHook } from "./hooks/use-workspace-cell-hook";
import { useWorkspaceDocumentHook } from "./hooks/use-workspace-document-hook";
import { useWorkspaceFileHook } from "./hooks/use-workspace-file-hook";
import { useWorkspaceFolderHook } from "./hooks/use-workspace-folder-hook";
import { useWorkspaceFormHook } from "./hooks/use-workspace-form-hook";
import { Breadcrumbs } from "./Metadata/Breadcrubms";
import { useWorkspace } from "./WorkspaceContext";

const WorkspaceFolderActions: React.FC<FolderActionsProps> = (props) => (
  <FolderActions {...props} useHook={useWorkspaceFolderHook} />
);
const WorkspaceFormActions: React.FC<FormActionsProps> = (props) => (
  <FormActions {...props} useHook={useWorkspaceFormHook} />
);
const WorkspaceDocumentActions: React.FC<DocumentActionsProps> = (props) => (
  <DocumentActions {...props} useHook={useWorkspaceDocumentHook} />
);
const WorkspaceFileActions: React.FC<FileActionsProps> = (props) => (
  <FileActions {...props} useHook={useWorkspaceFileHook} />
);

export const DealFolders: React.FC = () => {
  const { deal_id: dealIdParam } = useParams<{ deal_id: string }>();
  const dealId = dealIdParam ? Number(dealIdParam) : null;
  const {
    deals,
    getFolderMetadata,
    activeFilters,
    filtersUserInteracted,
    handleChangeFilter,
    currentFolder,
    globalFolderFilters,
    columnVisibility,
    toggleColumn,
    columnOrder,
    reorderColumns,
    selectedItems,
    setSelectedItems,
    handleRowClick,
    folderMetadata,
    folderMetadataLoading,
    refetchFolderMetadata,
    handleFetchNextPage,
    sortBy,
    setSortBy,
    globalFolderFiltersLoading,
    dispatchDetail
  } = useWorkspace();
  const { filtersList, dataroomOnlyKeys, isDataroom, isAggregateView, filterTypes } = useWorkspaceFilter({
    currentFolder,
    workspaceFiltersFromDB: globalFolderFilters
  });
  const { handleBulkDelete, handleBulkDownload } = useWorkspaceBulkActionsHook({
    selectedItems,
    setSelectedItems,
    useItemDealId: true
  });

  const { highlightedId, lastViewedIdRef: lastViewedFileIdRef, triggerHighlight } = useHighlightAfterClose();

  const {
    handleBulkView,
    handleBulkExtract,
    handleChangeClassification,
    handleChangeCurrency,
    handleChangeInvestor,
    handleChangeEffectiveDate,
    handleChangeStatus
  } = useBulkFileActionsHook({
    source: "deal",
    dealId,
    selectedItems,
    updateFile: (fileId, patch) => {
      dispatchDetail({
        key: "patch",
        id: fileId,
        entityType: EntityType.FILE,
        patch
      });
    },
    lastViewedFileIdRef,
    triggerHighlight,
    showErrors: false
  });

  const { onCreateNewFolder, onFileUpload } = useWorkspaceFolderHook({
    folder: { id: -1, title: "", is_private: false, parent_id: null, deal_id: null, user_id: -1, kind: "folder" }
  });

  const deal = useMemo(() => (dealId != null ? deals.find((d) => d.id === dealId) : null), [deals, dealId]);

  useEffect(() => {
    if (dealId != null) {
      getFolderMetadata();
    }
  }, [dealId, sortBy, activeFilters]);

  if (dealId == null) {
    return null;
  }

  const dealTitle = deal?.title ?? "Deal";
  const details = folderMetadata?.details ?? [];
  const kind = folderMetadata?.kind ?? "folder";

  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;

  return (
    <Stack
      direction="column"
      sx={{
        width: "100%",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        display: "flex"
      }}
      gap="16px"
    >
      <Stack direction="column" gap="16px" sx={{ flexShrink: 0 }}>
        <Breadcrumbs
          links={[
            { path: Routes.workspaceDeals, title: "Workspace" },
            { path: Routes.workspaceDeals, title: "Deals" },
            { path: `${Routes.workspace}/deal/${dealId}`, title: dealTitle }
          ]}
        />

        <Stack direction="row" alignItems="center" gap="23px" sx={{ width: "100%" }}>
          <Stack direction="row" alignItems="center" gap="10px" sx={{ flex: 1, minWidth: 0 }}>
            <WorkspaceFolderCloseIcon sx={{ fill: "#0A3142", fontSize: 20 }} />
            <Typography
              noWrap
              sx={{
                color: "#0a3142",
                fontWeight: 700,
                fontSize: 16,
                lineHeight: "24px"
              }}
            >
              {dealTitle || ""}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" gap="8px" sx={{ flexShrink: 0 }}>
            <ColumnVisibilityMenu
              columnVisibility={columnVisibility}
              onToggle={toggleColumn}
              kind={kind as FolderKind}
              hideShareColumn={Boolean(!dealId)}
            />
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" gap="10px" justifyContent="space-between">
          <WorkspaceFilter
            isLoading={globalFolderFiltersLoading}
            handleChangeFilter={handleChangeFilter}
            filtersList={filtersList}
            filterTypes={filterTypes}
            applyMode
            state={activeFilters}
            isDataroom={isDataroom}
            isAggregateView={isAggregateView}
            dataroomOnlyKeys={dataroomOnlyKeys}
            defaultFilterKeys={["document_type", "file_type", "investor"]}
            skipDefaults={filtersUserInteracted}
          />
          <BulkActionsBar
            selectedItems={selectedItems}
            onClose={() => setSelectedItems([])}
            onView={handleBulkView}
            onExtract={handleBulkExtract}
            onDownload={handleBulkDownload}
            onChangeClassification={handleChangeClassification}
            onChangeCurrency={handleChangeCurrency}
            onChangeInvestor={handleChangeInvestor}
            onChangeEffectiveDate={handleChangeEffectiveDate}
            onChangeStatus={handleChangeStatus}
            onDelete={handleBulkDelete}
          />
        </Stack>
      </Stack>

      <Stack
        sx={{
          overflow: "hidden",
          flexGrow: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column"
        }}
      >
        {details?.length || folderMetadataLoading ? (
          <MetadataTable
            kind={kind as FolderKind}
            data={details || []}
            isLoading={folderMetadataLoading}
            columnVisibility={columnVisibility}
            columnOrder={columnOrder}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            reorderColumns={reorderColumns}
            onRowClick={handleRowClick}
            onFetchNextPage={handleFetchNextPage}
            refetchFolderMetadata={refetchFolderMetadata}
            hideShareColumn={Boolean(!dealId)}
            useUpdateCellHookOverride={useWorkspaceCellHook}
            FolderActionsComponent={WorkspaceFolderActions}
            FormActionsComponent={WorkspaceFormActions}
            DocumentActionsComponent={WorkspaceDocumentActions}
            FileActionsComponent={WorkspaceFileActions}
            showDataroomColumns
            manualSorting
            onSortingChange={setSortBy}
            highlightedItemId={highlightedId}
          />
        ) : (
          <EmptyCard
            title={EMPTY_CARD_CONTENT.workspaceEmpty.title}
            description={{
              text: EMPTY_CARD_CONTENT.workspaceEmpty.description,
              externalText: EMPTY_CARD_CONTENT.workspaceEmpty.externalDescription,
              sx: { width: 388 }
            }}
            image={{
              src: EmptyCardDealWorkspace,
              sx: { width: 315, height: 140 }
            }}
            actionsElement={
              <>
                <Menu
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                  icon={
                    <Button
                      sx={{ textTransform: "none", bgcolor: "#071F35" }}
                      startIcon={<AddIcon />}
                      variant="contained"
                    >
                      Add item
                    </Button>
                  }
                  hidden={noAccessPermission || isViewerPermission || !!Object.keys(activeFilters).length}
                  keepTriggerEnabled
                  iconButtonProps={{ sx: { padding: 0, width: "auto" } }}
                  options={[
                    {
                      title: "Upload File",
                      icon: <FileUploadOutlinedIcon />,
                      onClick: onFileUpload,
                      hasLine: false
                    },
                    {
                      title: "New Folder",
                      icon: <CreateNewFolderOutlinedIcon />,
                      onClick: onCreateNewFolder
                    }
                  ]}
                />
              </>
            }
          />
        )}
      </Stack>
    </Stack>
  );
};
